import React, { createContext, ReactNode, useContext } from 'react';
import z from 'zod';
import { IntlProvider, useIntl } from 'react-intl';
import { MenuItem, Select } from '@mui/material';

import { messages } from '../messages';

import { useLocalStorage } from '../utils/use-session-storage';
import { first } from 'lodash';

type Props = {
  children: ReactNode;
};

type Locale = keyof typeof messages;

type LocaleContext = {
  setLocale: (locale: Locale) => void;
};

const localeContext = createContext<LocaleContext>({
  setLocale: () => {
    console.error('context not mounted');
  },
});

function getDefaultLocale(): Locale {
  const browserLocale = first(navigator.language.split('-')) ?? '';

  if (browserLocale in messages) {
    return browserLocale as Locale;
  }

  return 'cs';
}

const defaultLocale = getDefaultLocale();

export const LocaleProvider = ({ children }: Props) => {
  const [locale, setLocale] = useLocalStorage(
    z.string(),
    'locale',
    defaultLocale
  );

  const localeMessages =
    locale in messages ? messages[locale as Locale] : undefined;

  return (
    <localeContext.Provider value={{ setLocale }}>
      <IntlProvider
        locale={locale}
        messages={localeMessages}
        defaultLocale={defaultLocale}
      >
        <>{children}</>
      </IntlProvider>
    </localeContext.Provider>
  );
};

export const LocaleSelect = () => {
  const { locale } = useIntl();
  const { setLocale } = useContext(localeContext);

  return (
    <Select
      sx={{ ml: 'auto' }}
      value={locale}
      onChange={(e) => {
        setLocale(e.target.value as Locale);
      }}
    >
      {Object.keys(messages).map((l) => (
        <MenuItem value={l} key={l}>
          {l.toUpperCase()}
        </MenuItem>
      ))}
    </Select>
  );
};
